/**
 * Product Sticky Column (Desktop) — scroll-direction-aware "follow" sticky.
 *
 * CSS provides `position: sticky; top: <offset>; align-self: start` as the no-JS
 * baseline. That works on its own ONLY when the sticky column is shorter than its
 * sibling (the sibling supplies the scroll distance). This module augments it so
 * the sticky column also behaves correctly when it is TALLER than the viewport:
 * the column follows the page scroll (revealing its top when scrolling up and its
 * bottom when scrolling down) using a pure page-driven translateY — NO inner
 * overflow / scrollbar.
 *
 * Behaviour:
 *   - Non-sticky column scrolls with the page normally.
 *   - Sticky column pins at the top offset while it (and the row) still has travel.
 *   - When the sticky column is taller than the viewport, continued scrolling lets
 *     it scroll through its own content so its bottom becomes readable, then it
 *     bottom-pins; scrolling back up reveals its top again.
 *   - Works for both `product-page--sticky-info` (right) and
 *     `product-page--sticky-media` (left): the logic targets whichever column is sticky.
 *
 * Desktop only (>= 990px, the theme's two-column breakpoint). Disabled and cleared
 * below the breakpoint and when `sticky_column` is `none`. rAF-throttled scroll +
 * debounced resize + ResizeObserver. No dependencies. Reuses the same offset the CSS
 * uses (`--untamed-fixed-header-offset` + `--product-sticky-offset`).
 */
(function () {
  'use strict';
  if (window.UntamedProductSticky) return;

  var MIN_DESKTOP = 990; // matches @media (min-width: 990px)

  var page = null;
  var container = null;
  var column = null;
  var mode = null;
  var rafId = 0;
  var resizeId = 0;
  var lastScroll = 0;
  var prevVTop = 0;
  var ro = null;

  function num(v) { var n = parseFloat(v); return isNaN(n) ? 0 : n; }
  function clamp(v, lo, hi) { return v < lo ? lo : (v > hi ? hi : v); }
  function isDesktop() { return window.matchMedia('(min-width: ' + MIN_DESKTOP + 'px)').matches; }

  function offsets() {
    var header = num(getComputedStyle(document.documentElement).getPropertyValue('--untamed-fixed-header-offset'));
    var sticky = 16;
    if (column) {
      var v = getComputedStyle(column).getPropertyValue('--product-sticky-offset');
      if (v) sticky = num(v);
    }
    return { top: header + sticky, bottom: sticky };
  }

  function resolve() {
    page = document.querySelector('.product-page');
    container = document.querySelector('.product-page__inner');
    column = null;
    mode = null;
    if (!page || !container) return false;
    if (page.classList.contains('product-page--sticky-info')) {
      column = container.querySelector(':scope > .product-info');
      mode = 'info';
    } else if (page.classList.contains('product-page--sticky-media')) {
      column = container.querySelector(':scope > .product-media');
      mode = 'media';
    }
    return !!column;
  }

  // Whether JS is currently driving the column (translateY follow). When the
  // column FITS in the viewport we stay out of the way entirely and let the
  // CSS `position: sticky` rule pin it — the compositor moves it with zero
  // frame lag, where the rAF-driven transform always trailed native scroll by
  // up to a frame (the main source of the visible jitter).
  var jsMode = false;

  function clearInline() {
    if (column) {
      column.style.transform = '';
      column.style.willChange = '';
      column.style.position = '';
    }
    jsMode = false;
  }

  function update() {
    rafId = 0;
    if (!column) return;
    if (!isDesktop() || !mode) { clearInline(); return; }

    var o = offsets();
    var vh = window.innerHeight;
    var colH = column.offsetHeight;

    // Fits in the viewport → native CSS sticky. No inline styles at all.
    if (colH + o.top + o.bottom <= vh) {
      if (jsMode) clearInline();
      return;
    }

    // Taller than the viewport → JS direction-aware follow.
    if (!jsMode) {
      jsMode = true;
      // Seed tracking from where the column ACTUALLY is right now (its rect
      // includes any pin/translate), so mode changes never snap it.
      lastScroll = window.pageYOffset;
      prevVTop = column.getBoundingClientRect().top;
      // Keep the compositor layer alive for the whole JS session — toggling
      // will-change at the t=0 boundary created/destroyed the layer
      // repeatedly near the top of the page (repaint jank).
      column.style.willChange = 'transform';
    }

    var cRect = container.getBoundingClientRect();
    var naturalVTop = cRect.top;                       // grid items start at the container content top (align-self:start)
    var containerH = container.offsetHeight;
    var room = Math.max(0, containerH - colH);         // how far the column may translate within its grid cell

    var maxVTop = o.top;                               // top-aligned pin
    var minVTop = Math.min(o.top, vh - o.bottom - colH); // bottom-aligned pin (negative when taller than viewport)

    var s = window.pageYOffset;
    var delta = s - lastScroll;

    // Direction-aware follow: the column's viewport-top moves opposite to scroll,
    // clamped between the top-pin and bottom-pin positions.
    var vTop = clamp(prevVTop - delta, minVTop, maxVTop);

    // Convert the desired viewport-top into a translate, clamped so the column
    // never escapes its grid cell (keeps it inside the product section).
    // Whole pixels: fractional translates shimmer text/images on non-retina.
    var t = Math.round(clamp(vTop - naturalVTop, 0, room));

    // Re-derive the achieved viewport-top after the cell clamp so direction tracking stays exact.
    prevVTop = naturalVTop + t;
    lastScroll = s;

    // Neutralise native sticky while JS drives the position, to avoid double offset.
    column.style.position = 'static';
    column.style.transform = t > 0 ? 'translateY(' + t + 'px)' : '';
  }

  function onScroll() { if (!rafId) rafId = requestAnimationFrame(update); }

  function reset() {
    if (!column) return;
    lastScroll = window.pageYOffset;
    // Preserve the column's CURRENT position (rect includes any transform):
    // offset/height changes re-clamp from here instead of snapping the
    // column back to the container top mid-scroll.
    prevVTop = column.getBoundingClientRect().top;
    update();
  }

  function rebind() {
    // Height/offset changes used to clear the transform and re-seed from the
    // container top, visibly snapping the gallery whenever the other column
    // grew or shrank (accordion toggles, lazy images). Keep the column where
    // it is: only re-resolve elements, then re-clamp from the current spot.
    var prevColumn = column;
    var keepTop = column ? column.getBoundingClientRect().top : null;
    if (ro) { ro.disconnect(); ro = null; }
    if (!resolve()) { if (prevColumn) { prevColumn.style.transform = ''; prevColumn.style.willChange = ''; prevColumn.style.position = ''; } jsMode = false; return; }
    if (column !== prevColumn) { jsMode = false; keepTop = null; }
    lastScroll = window.pageYOffset;
    prevVTop = keepTop != null ? keepTop : container.getBoundingClientRect().top;
    update();
    if ('ResizeObserver' in window) {
      ro = new ResizeObserver(function () { onResize(); });
      ro.observe(container);
      ro.observe(column);
    }
  }

  function onResize() {
    if (resizeId) clearTimeout(resizeId);
    resizeId = setTimeout(rebind, 150);
  }

  function init() {
    if (!resolve()) return;
    reset();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);
    window.addEventListener('untamed:offset-changed', reset);
    document.addEventListener('shopify:section:load', rebind);
    if ('ResizeObserver' in window) {
      ro = new ResizeObserver(function () { onResize(); });
      ro.observe(container);
      ro.observe(column);
    }
  }

  window.UntamedProductSticky = { update: update, reset: reset, rebind: rebind };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
